import type {ExpandedState} from '@tanstack/react-table'
import {useMemo} from 'react'

import {useTableCopilotTasks} from '../../../features/copilot-tasks/use-table-copilot-tasks'
import {useHorizontalGroupedBy} from '../../../features/grouping/hooks/use-horizontal-grouped-by'
import {useExpandedRows} from '../../../features/sub-issues/use-expanded-rows'
import {useTableHierarchy} from '../../../features/sub-issues/use-table-hierarchy'
import {usePaginatedMemexItemsQuery} from '../../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import {useSortedBy} from '../../use-sorted-by'
import {groupedRowId} from '../helpers/get-row-id'

/**
 * Returns the state slices required by `@tanstack/react-table` that are
 * managed by Memex outside of the table.
 *
 * @link [Controlled state docs](https://tanstack.com/table/v8/docs/framework/react/guide/table-state#controlled-state)
 */
export const useControlledTableState = () => {
  const {groupedByColumnId, collapsedGroups} = useHorizontalGroupedBy()
  const {expandedRows} = useExpandedRows()
  const {hierarchyEnabled} = useTableHierarchy()
  const {copilotTasksEnabled} = useTableCopilotTasks()
  const grouping = useMemo(() => (groupedByColumnId ? [`${groupedByColumnId}`] : []), [groupedByColumnId])
  const {groupedItemQueries, queriesForGroups, groupsById, subIssuesByParentId} = usePaginatedMemexItemsQuery()
  const groupQueryData = useMemo(
    () => ({
      groupedItemQueries,
      queriesForGroups,
      groupsById,
    }),
    [groupedItemQueries, queriesForGroups, groupsById],
  )
  const subIssuesQueryData = useMemo(
    () => ({
      subIssuesByParentId: hierarchyEnabled || copilotTasksEnabled ? subIssuesByParentId : {},
      copilotTasksEnabled,
    }),
    [subIssuesByParentId, hierarchyEnabled, copilotTasksEnabled],
  )
  const expanded: ExpandedState = useMemo(() => {
    if (hierarchyEnabled || copilotTasksEnabled) {
      const groups = queriesForGroups.flatMap(query => query?.data?.groups || [])
      const expandedGroups: ExpandedState = !groupedByColumnId
        ? // If grouped column, just return an empty object (i.e., all groups collapsed)
          {}
        : collapsedGroups.length === 0
          ? // If no collapsed groups, mark all grouped rows as expanded
            Object.fromEntries(groups.map(group => [groupedRowId(groupedByColumnId, group.groupId), true]))
          : Object.fromEntries(
              groups.map(group => {
                const rowId = groupedRowId(groupedByColumnId, group.groupId)
                const rowExpanded = !collapsedGroups.includes(group.groupId)
                return [rowId, rowExpanded]
              }),
            )
      const expandedRowState: ExpandedState = Object.fromEntries(expandedRows.map(rowId => [rowId, true]))
      return {...expandedGroups, ...expandedRowState}
    } else {
      if (!groupedByColumnId || collapsedGroups.length === 0) return true
      return queriesForGroups
        .flatMap(query => query?.data?.groups || [])
        .reduce(
          (acc, group) => {
            // Note: collapsedGroups is a list of server-provided groupIds,
            // but react table expects an object where the keys are the grouped row IDs.
            const rowId = groupedRowId(groupedByColumnId, group.groupId)
            acc[rowId] = !collapsedGroups.includes(group.groupId)
            return acc
          },
          {} as Record<string, boolean>,
        )
    }
  }, [collapsedGroups, expandedRows, groupedByColumnId, hierarchyEnabled, copilotTasksEnabled, queriesForGroups])

  const {sorts} = useSortedBy()
  const sorting = useMemo(
    () => sorts.map(sort => ({id: sort.column.id.toString(), desc: sort.direction === 'desc'})),
    [sorts],
  )

  return {
    expanded,
    grouping,
    groupQueryData,
    sorting,
    subIssuesQueryData,
  }
}

/**
 * Memex manages several slices of state outsides of `@tanstack/react-table`.
 * Unlike the controlled state implementation described in the official docs,
 * we've chosen not to implement `on*Change` handlers. These handlers are called
 * by the table instance when a React Table method is called that should change
 * the state slice.
 * e.g., `column.toggleSorting()`
 *
 * Instead, we bypass React Table for these user interactions by using our own hooks
 * to manage these state slices outside of the table and then pass the updated state
 * back into the table via the `state` option.
 * e.g., `useSortedBy().setColumnSort(column, direction)`
 *
 * We've repurposed the `on*Change` handlers to throw errors and direct developers
 * to the appropriate alternate hook. Without these errors, calls to methods like
 * `column.toggleSorting()` would fail silently, which is confusing.
 *
 * @link [Controlled state docs](https://tanstack.com/table/v8/docs/framework/react/guide/table-state#controlled-state)
 */
export const controlledStateErrorWarnings = {
  onColumnVisibilityChange: () => {
    controlledStateError('columnVisibility', 'useVisibleFields')
  },
  onColumnOrderChange: () => {
    controlledStateError('columnOrder', 'useVisibleFields')
  },
  onExpandedChange: () => {
    controlledStateError('expanded', 'useHorizontalGroupedBy')
  },
  onGroupingChange: () => {
    controlledStateError('grouping', 'useHorizontalGroupedBy')
  },
  onSortingChange: () => {
    controlledStateError('sorting', 'useSortedBy')
  },
}

/**
 * @param stateName - name of controlled state slice
 * @param hookName - name of Memex hook to use instead
 */
const controlledStateError = (stateName: string, hookName: string) => {
  throw new Error(
    `Changing state ${stateName} through @tanstack/react-table APIs is not supported. Use ${hookName} hook instead.`,
  )
}
